import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import Api from "../../api";
import { HOME_MENU } from "../../constants/homeMenu";
import { useAuth } from "../../context/AuthContext";

const MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const WEEKDAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const UNIT_COLOR = { it: "#fb7185", lab: "#a78bfa", sarpra: "#fbbf24" };
const UNIT_LABEL = { it: "IT", lab: "Lab", sarpra: "Sarpra" };
const STATUS_LABEL = {
  planning: "Planning",
  terlaksana: "Terlaksana",
  tertunda: "Tertunda",
  batal: "Batal",
  proses: "Proses",
  selesai: "Selesai",
  pending: "Pending",
  terjadwal: "Terjadwal",
};
const QUARTERS = [
  { id: 1, label: "TW 1", range: "Jan–Mar" },
  { id: 2, label: "TW 2", range: "Apr–Jun" },
  { id: 3, label: "TW 3", range: "Jul–Sep" },
  { id: 4, label: "TW 4", range: "Okt–Des" },
];
const numberFmt = new Intl.NumberFormat("id-ID");

function pad(value) {
  return String(value).padStart(2, "0");
}

function dateKey(year, monthIndex, day) {
  return `${year}-${pad(monthIndex + 1)}-${pad(day)}`;
}

function sliceDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function firstName(name) {
  const part = String(name || "").trim().split(/\s+/)[0];
  return part || "";
}

function waktuHari(date) {
  const hour = date.getHours();
  if (hour < 11) return "pagi";
  if (hour < 15) return "siang";
  if (hour < 18) return "sore";
  return "malam";
}

function progressOf(status) {
  if (status === "selesai") return 100;
  if (status === "proses") return 62;
  if (status === "terjadwal") return 42;
  if (status === "pending") return 28;
  return 18;
}

function barColor(status) {
  if (status === "selesai") return "bg-emerald-500";
  if (status === "pending") return "bg-amber-500";
  return "bg-primary";
}

function Ring({ percent, color, caption }) {
  const safe = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const radius = 32;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (safe / 100) * circ;
  return (
    <div className="flex items-center gap-3">
      <div className="relative h-[4.6rem] w-[4.6rem] shrink-0">
        <svg viewBox="0 0 80 80" className="h-full w-full -rotate-90">
          <circle cx="40" cy="40" r={radius} fill="none" stroke="#f1f0f8" strokeWidth="8" />
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-slate-800">
          {safe}%
        </span>
      </div>
      <p className="text-xs leading-snug text-slate-500">{caption}</p>
    </div>
  );
}

function TargetRing({ percent }) {
  const safe = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const radius = 38;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (safe / 100) * circ;
  const color = safe >= 80 ? "#059669" : safe >= 40 ? "#d97706" : "#e11d48";
  return (
    <div className="relative h-[7.2rem] w-[7.2rem] shrink-0">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#f1f0f8" strokeWidth="10" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-semibold tracking-tight text-slate-900">{safe}%</span>
        <span className="text-[10px] font-medium text-slate-400">target</span>
      </div>
    </div>
  );
}
function ChartTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="rounded-xl bg-slate-900 px-3 py-2 text-xs text-white shadow-lg">
      <p className="mb-1 font-semibold">{row?.label}</p>
      {payload.map((item) => (
        <p key={item.dataKey}>
          {item.name}: {numberFmt.format(item.value || 0)}
        </p>
      ))}
    </div>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState([]);
  const [grid, setGrid] = useState(null);
  const [coe, setCoe] = useState(null);
  const [coeRows, setCoeRows] = useState([]);
  const [atkRows, setAtkRows] = useState([]);
  const [atkMasuk, setAtkMasuk] = useState([]);
  const [atkKeluar, setAtkKeluar] = useState([]);
  const [persediaanRows, setPersediaanRows] = useState([]);
  const [materialRows, setMaterialRows] = useState([]);
  const [roomWatch, setRoomWatch] = useState(null);
  const [roomQuarter, setRoomQuarter] = useState(() => Math.floor(new Date().getMonth() / 3) + 1);
  const [roomBusy, setRoomBusy] = useState(true);
  const [materialOpen, setMaterialOpen] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      const [trendRes, coeRes, listRes, atkRes, asetRes, bangunanRes, persediaanRes, materialRes] = await Promise.allSettled([
        Api.get("/mon/statistik/trend-bulanan"),
        Api.get("/sp/coe/dashboard"),
        Api.get("/sp/coe/tahunan", { params: { page: 1, per_page: 200 } }),
        Api.get("/mon/atk", { params: { page: 1, per_page: 100, sort_by: "nama", sort_order: "asc" } }),
        Api.get("/mon/perawatan-aset"),
        Api.get("/mon/perawatan-bangunan"),
        Api.get("/mon/persediaan/ringkasan"),
        Api.get("/mon/material/ringkasan"),
      ]);
      if (ignore) return;
      if (trendRes.status === "fulfilled") setTrend(trendRes.value.data?.data || []);
      if (coeRes.status === "fulfilled") setCoe(coeRes.value.data?.data || null);
      if (listRes.status === "fulfilled") setCoeRows(listRes.value.data?.data || []);
      if (atkRes.status === "fulfilled") {
        setAtkRows(atkRes.value.data?.data || []);
        setAtkMasuk(atkRes.value.data?.masuk || []);
        setAtkKeluar(atkRes.value.data?.keluar || []);
      }
      if (persediaanRes.status === "fulfilled") {
        setPersediaanRows(persediaanRes.value.data?.data || []);
      }
      if (materialRes.status === "fulfilled") {
        setMaterialRows(materialRes.value.data?.data || []);
      }
      if (asetRes.status === "fulfilled" || bangunanRes.status === "fulfilled") {
        setGrid({
          perawatanAset: asetRes.status === "fulfilled" ? asetRes.value.data?.data || [] : [],
          perbaikanGedung: bangunanRes.status === "fulfilled" ? bangunanRes.value.data?.data || [] : [],
        });
      }
      setLoading(false);
    })();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setRoomBusy(true);
      try {
        const res = await Api.get("/mon/statistik/monitoring-ruangan", {
          params: { tahun: new Date().getFullYear(), triwulan: roomQuarter },
        });
        if (ignore) return;
        setRoomWatch({
          periode: res.data?.periode || null,
          ringkasan: res.data?.ringkasan || { total: 0, sudah: 0, belum: 0, persen: 0 },
          data: res.data?.data || [],
        });
      } catch (error) {
        if (!ignore) setRoomWatch({ periode: null, ringkasan: { total: 0, sudah: 0, belum: 0, persen: 0 }, data: [] });
      } finally {
        if (!ignore) setRoomBusy(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [roomQuarter]);

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const todayKey = dateKey(year, month, now.getDate());
  const nama = firstName(currentUser?.name);

  const chartData = useMemo(
    () => (Array.isArray(trend) ? trend.slice(0, month + 1) : []).map((row) => ({
      ...row,
      label: SHORT_MONTHS[(row.bulan_num || 1) - 1] || row.bulan,
    })),
    [trend, month]
  );

  const summary = coe?.summary || {};
  const upcoming = Array.isArray(coe?.upcoming) ? coe.upcoming.slice(0, 3) : [];
  const recentCoe = useMemo(
    () => [...coeRows].sort((a, b) => sliceDate(b.tgl).localeCompare(sliceDate(a.tgl))).slice(0, 3),
    [coeRows]
  );
  const agenda = upcoming.length ? upcoming : recentCoe;
  const agendaTerakhir = upcoming.length === 0 && agenda.length > 0;
  const coeTotal = summary.total || 0;
  const coePercent = coeTotal ? Math.round(((summary.terlaksana || 0) / coeTotal) * 100) : 0;

  const dayMarks = useMemo(() => {
    const marks = {};
    const monthStart = dateKey(year, month, 1);
    const lastDay = new Date(year, month + 1, 0).getDate();
    const monthEnd = dateKey(year, month, lastDay);
    coeRows.forEach((item) => {
      const start = sliceDate(item.tgl);
      const end = sliceDate(item.end) || start;
      if (!start || end < monthStart || start > monthEnd) return;
      const from = start < monthStart ? 1 : Number(start.slice(8, 10));
      const to = end > monthEnd ? lastDay : Number(end.slice(8, 10));
      for (let day = from; day <= to; day += 1) {
        const key = dateKey(year, month, day);
        if (!marks[key]) marks[key] = [];
        if (item.unit && !marks[key].includes(item.unit)) marks[key].push(item.unit);
      }
    });
    return marks;
  }, [coeRows, year, month]);

  const calendarCells = useMemo(() => {
    const first = new Date(year, month, 1);
    const lead = (first.getDay() + 6) % 7;
    const days = new Date(year, month + 1, 0).getDate();
    const cells = Array.from({ length: lead }, () => null);
    for (let day = 1; day <= days; day += 1) cells.push(day);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const tasks = useMemo(() => {
    const aset = (grid?.perawatanAset || []).map((row) => ({
      id: `aset-${row.id}`,
      title: row.jenis_kerusakan || "Perawatan aset",
      meta: `Perawatan aset${sliceDate(row.tgl_masuk) ? ` · ${Number(sliceDate(row.tgl_masuk).slice(8, 10))} ${SHORT_MONTHS[Number(sliceDate(row.tgl_masuk).slice(5, 7)) - 1]} ${sliceDate(row.tgl_masuk).slice(0, 4)}` : ""}`,
      status: row.status || "proses",
      progress: progressOf(row.status),
      to: "/perawatan-aset",
      date: sliceDate(row.tgl_masuk),
    }));
    const bangunan = (grid?.perbaikanGedung || []).map((row) => ({
      id: `bangunan-${row.id}`,
      title: row.nama_bagian || row.jenis_kerusakan || "Perawatan bangunan",
      meta: `Bangunan${sliceDate(row.tgl_masuk) ? ` · ${Number(sliceDate(row.tgl_masuk).slice(8, 10))} ${SHORT_MONTHS[Number(sliceDate(row.tgl_masuk).slice(5, 7)) - 1]} ${sliceDate(row.tgl_masuk).slice(0, 4)}` : ""}`,
      status: row.status || "proses",
      progress: progressOf(row.status),
      to: "/perawatan-bangunan",
      date: sliceDate(row.tgl_masuk),
    }));
    const all = [...aset, ...bangunan].sort((a, b) => {
      if ((a.progress === 100) !== (b.progress === 100)) return a.progress === 100 ? 1 : -1;
      return String(b.date).localeCompare(String(a.date));
    });
    const open = all.filter((item) => item.progress < 100);
    return (open.length ? open : all).slice(0, 4);
  }, [grid]);

  const stocks = useMemo(() => {
    const totalMasuk = (id) =>
      atkMasuk
        .filter((row) => Number(row.atk_id) === Number(id))
        .reduce((sum, row) => sum + Number(row.jml || 0), 0);
    const totalKeluar = (id) =>
      atkKeluar
        .filter((row) => Number(row.atk_id) === Number(id))
        .reduce((sum, row) => sum + Number(row.vol || 0), 0);
    return atkRows
      .map((item) => ({
        id: item.id,
        name: item.nabar || "ATK",
        akhir: totalMasuk(item.id) - totalKeluar(item.id),
      }))
      .sort((a, b) => a.akhir - b.akhir);
  }, [atkRows, atkMasuk, atkKeluar]);
  const lowStocks = stocks.filter((item) => item.akhir <= 20).slice(0, 3);
  const stockShown = (lowStocks.length ? lowStocks : stocks.slice(0, 3));
  const aman = stocks.filter((item) => item.akhir > 20).length;
  const stockPercent = stocks.length ? Math.round((aman / stocks.length) * 100) : 0;

  const materialWatch = useMemo(() => {
    const ranked = materialRows
      .map((item) => {
        const stok = Number(item.stok_gudang) || 0;
        const minimal = Number(item.stok_minimal) || 0;
        const status = stok <= 0 ? "habis" : stok <= minimal ? "menipis" : "aman";
        const level = minimal > 0 ? Math.max(0, Math.min(100, Math.round((stok / minimal) * 100))) : stok > 0 ? 100 : 0;
        return { ...item, stok, minimal, status, level };
      })
      .sort((a, b) => {
        const order = { habis: 0, menipis: 1, aman: 2 };
        if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
        return a.stok - b.stok;
      });
    return {
      rows: ranked,
      habis: ranked.filter((item) => item.status === "habis").length,
      menipis: ranked.filter((item) => item.status === "menipis").length,
      aman: ranked.filter((item) => item.status === "aman").length,
    };
  }, [materialRows]);

  const lowMaterials = materialWatch.rows.filter((item) => item.status !== "aman");

  const roomGroups = useMemo(() => {
    const rows = roomWatch?.data || [];
    const map = new Map();
    rows.forEach((row) => {
      const key = row.gedung || "-";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return [...map.entries()].map(([gedung, rooms]) => ({ gedung, rooms }));
  }, [roomWatch]);
  const roomRows = roomWatch?.data || [];
  const roomSudah = roomWatch?.ringkasan?.sudah ?? roomRows.filter((room) => room.sudah).length;
  const roomTotal = roomWatch?.ringkasan?.total ?? roomRows.length;
  const roomPercent = roomTotal ? Math.round((roomSudah / roomTotal) * 100) : 0;
  const activeQuarter = QUARTERS.find((item) => item.id === roomQuarter) || QUARTERS[0];

  return (
    <div className="space-y-4 sm:space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">SIP Monitor</p>
        <h1 className="mt-1 text-[1.7rem] font-semibold tracking-tight text-slate-900 sm:text-3xl">
          {nama ? `Halo, ${nama}` : "Halo"}
        </h1>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500">
          Selamat {waktuHari(now)}. Ini agenda, stok, dan pekerjaan yang perlu dilihat hari ini.
        </p>
        <p className="mt-3 text-xs text-slate-400">
          {now.getDate()} {MONTHS[month]} {year}
        </p>
      </div>

      <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)] sm:p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Aplikasi</h2>
        <div className="grid grid-cols-4 gap-x-2 gap-y-4 sm:grid-cols-5 tablet:grid-cols-6 xl:grid-cols-8">
          {HOME_MENU.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.link}
                to={item.link}
                title={item.description}
                aria-label={item.description ? `${item.title}. ${item.description}` : item.title}
                className="flex min-w-0 flex-col items-center gap-1.5 rounded-2xl px-1 py-1 text-center transition hover:bg-slate-50 active:scale-[0.98]"
              >
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.15rem] shadow-[0_6px_14px_rgba(80,70,140,0.12)] sm:h-14 sm:w-14 ${item.tone}`}>
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </span>
                <span className="line-clamp-2 w-full text-[11px] font-medium leading-tight text-slate-700">
                  {item.title}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)]">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">{agendaTerakhir ? "Kegiatan terakhir" : "Agenda"}</h2>
            <Link to="/coe" className="text-xs font-medium text-primary">Lihat COE</Link>
          </div>
          {loading ? (
            <p className="text-sm text-slate-400">Memuat agenda…</p>
          ) : agenda.length === 0 ? (
            <p className="rounded-2xl bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
              Belum ada kegiatan COE.
            </p>
          ) : (
            <ul className="space-y-2">
              {agenda.map((item) => {
                const key = sliceDate(item.tgl);
                const day = key ? Number(key.slice(8, 10)) : "–";
                const mon = key ? SHORT_MONTHS[Number(key.slice(5, 7)) - 1] : "";
                return (
                  <li key={item.id}>
                    <Link to="/coe" className="flex items-center gap-3 rounded-2xl px-1 py-1.5 transition hover:bg-slate-50">
                      <span className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-slate-100">
                        <span className="text-[10px] font-semibold uppercase text-slate-400">{mon}</span>
                        <span className="text-base font-semibold leading-none text-slate-800">{day}</span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-800">{item.kegiatan}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                            {UNIT_LABEL[item.unit] || item.unit || "Unit"}
                          </span>
                          <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                            {STATUS_LABEL[item.status] || item.status}
                          </span>
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mt-3 text-[11px] text-slate-400">
            {loading
              ? "…"
              : agendaTerakhir
                ? "Tidak ada yang dijadwalkan ke depan. Ini kegiatan yang sudah tercatat."
                : `${summary.minggu_ini || 0} kegiatan dalam 7 hari ke depan`}
          </p>
        </section>

        <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)] xl:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Pekerjaan</h2>
            <span className="text-[11px] text-slate-400">
              {tasks.some((item) => item.progress < 100) ? "Yang masih berjalan" : "Terakhir selesai"}
            </span>
          </div>
          {loading ? (
            <p className="text-sm text-slate-400">Memuat pekerjaan…</p>
          ) : tasks.length === 0 ? (
            <p className="rounded-2xl bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
              Belum ada perawatan tercatat.
            </p>
          ) : (
            <ul className="space-y-3">
              {tasks.map((item) => (
                <li key={item.id}>
                  <Link to={item.to} className="block rounded-2xl px-1 py-1 transition hover:bg-slate-50">
                    <span className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-slate-800">{item.title}</span>
                        <span className="text-[11px] text-slate-400">{item.meta}</span>
                      </span>
                      <span className="shrink-0 text-[11px] font-medium text-slate-500">
                        {STATUS_LABEL[item.status] || item.status} · {item.progress}%
                      </span>
                    </span>
                    <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <span className={`block h-full rounded-full ${barColor(item.status)}`} style={{ width: `${item.progress}%` }} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)] lg:col-span-2 xl:col-span-3">
          <div className="grid grid-cols-1 items-stretch gap-5 tablet:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-800">{MONTHS[month]} {year}</h2>
                <Link to="/coe" className="text-xs font-medium text-primary">Kalender</Link>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium text-slate-400">
                {WEEKDAYS.map((day) => (
                  <span key={day} className="py-1">{day}</span>
                ))}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {calendarCells.map((day, index) => {
                  if (!day) return <span key={`empty-${index}`} />;
                  const key = dateKey(year, month, day);
                  const units = dayMarks[key] || [];
                  const isToday = key === todayKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`coe-day relative mx-auto flex h-10 w-10 items-center justify-center rounded-full text-xs ${
                        isToday ? "bg-primary font-semibold text-white" : "text-slate-700 hover:bg-primary-50"
                      }`}
                      onClick={() => navigate("/coe")}
                      aria-label={units.length ? `${day} ${MONTHS[month]}, ada kegiatan` : `${day} ${MONTHS[month]}`}
                    >
                      {day}
                      {units.length > 0 && (
                        <span className="absolute bottom-0.5 flex gap-0.5">
                          {units.slice(0, 3).map((unit) => (
                            <i
                              key={unit}
                              className="h-1 w-1 rounded-full"
                              style={{ background: isToday ? "#fff" : UNIT_COLOR[unit] || "#6E63F6" }}
                            />
                          ))}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="min-w-0 border-t border-slate-100 pt-4 tablet:border-l tablet:border-t-0 tablet:pl-5 tablet:pt-0">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Persediaan gudang</h2>
                  <p className="mt-0.5 text-[11px] text-slate-400">Sisa di gudang dan yang sudah didistribusikan</p>
                </div>
                <Link to="/kartu-persediaan" className="shrink-0 text-xs font-medium text-primary">Kartu</Link>
              </div>
              {loading ? (
                <p className="text-sm text-slate-400">Memuat persediaan…</p>
              ) : persediaanRows.length === 0 ? (
                <p className="rounded-2xl bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                  Belum ada barang persediaan.
                </p>
              ) : (
                <div className="max-h-80 min-h-[17.5rem] overflow-auto">
                  <table className="w-full min-w-[18rem] text-left text-sm">
                    <thead className="sticky top-0 bg-white text-[11px] uppercase tracking-wide text-slate-400">
                      <tr>
                        <th className="pb-2 pr-3 font-medium">Barang</th>
                        <th className="pb-2 pr-3 text-right font-medium">Gudang</th>
                        <th className="pb-2 text-right font-medium">Distribusi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {persediaanRows.map((item) => (
                        <tr key={item.id} className="border-t border-slate-100">
                          <td className="py-2 pr-3">
                            <span className="block truncate font-medium text-slate-800">{item.nama}</span>
                            <span className="text-[11px] text-slate-400">{item.kode}</span>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                            {numberFmt.format(Number(item.stok_gudang) || 0)}
                            <span className="ml-1 text-[11px] text-slate-400">{item.satuan || ""}</span>
                          </td>
                          <td className="py-2 text-right tabular-nums text-slate-700">
                            {numberFmt.format(Number(item.terdistribusi) || 0)}
                            <span className="ml-1 text-[11px] text-slate-400">{item.satuan || ""}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)] sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Target monitoring ruangan</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {activeQuarter.range} {year} · {roomSudah} dari {roomTotal} ruangan
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {QUARTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`room-tw ${roomQuarter === item.id ? "is-active" : ""}`}
                onClick={() => setRoomQuarter(item.id)}
              >
                {item.label}
                <small>{item.range}</small>
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 items-center gap-4 tablet:grid-cols-[9.5rem_minmax(0,1fr)]">
          <div className="flex items-center justify-center">
            <TargetRing percent={roomBusy ? 0 : roomPercent} />
          </div>
          <div className="min-w-0">
            <div className="mb-2 flex items-center justify-between text-[11px] font-medium">
              <span className="text-emerald-700">Sudah {roomSudah}</span>
              <Link to="/monitoring" className="text-primary">Buka monitoring</Link>
              <span className="text-rose-600">Belum {Math.max(roomTotal - roomSudah, 0)}</span>
            </div>
            {roomBusy ? (
              <p className="text-sm text-slate-400">Memuat ruangan…</p>
            ) : roomGroups.length === 0 ? (
              <p className="text-sm text-slate-500">Belum ada data ruangan.</p>
            ) : (
              <div className="max-h-36 space-y-2 overflow-y-auto pr-1">
                {roomGroups.map((group) => (
                  <div key={group.gedung} className="flex items-start gap-2">
                    <span className="mt-0.5 w-14 shrink-0 text-[11px] font-semibold text-slate-400">Gd. {group.gedung}</span>
                    <div className="flex min-w-0 flex-wrap gap-1">
                      {group.rooms.map((room) => (
                        <span
                          key={room.id}
                          title={room.sudah ? "Sudah dimonitor pada triwulan ini" : "Belum dimonitor pada triwulan ini"}
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            room.sudah ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {room.ruangan}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)]">
          <h2 className="mb-4 text-sm font-semibold text-slate-800">Kondisi sekarang</h2>
          <div className="space-y-4">
            <Ring
              percent={coePercent}
              color="#6E63F6"
              caption={loading ? "Memuat COE…" : `${summary.terlaksana || 0} dari ${coeTotal} kegiatan COE sudah terlaksana`}
            />
            <Ring
              percent={stockPercent}
              color="#059669"
              caption={loading ? "Memuat stok…" : stocks.length ? `${aman} dari ${stocks.length} item alat tulis stoknya aman` : "Data stok belum tersedia"}
            />
          </div>
        </section>

        <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)]">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">
              {lowStocks.length ? "Stok yang menipis" : "Stok alat tulis"}
            </h2>
            <Link to="/atk" className="text-xs font-medium text-primary">ATK</Link>
          </div>
          {loading ? (
            <p className="text-sm text-slate-400">Memuat stok…</p>
          ) : stockShown.length === 0 ? (
            <p className="text-sm text-slate-500">Belum ada data alat tulis.</p>
          ) : (
            <ul className="space-y-2">
              {stockShown.map((item) => (
                <li key={item.id}>
                  <Link to="/atk" className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2.5">
                    <span className="truncate text-sm text-slate-700">{item.name}</span>
                    <span className={`shrink-0 text-sm font-semibold tabular-nums ${item.akhir <= 10 ? "text-rose-600" : item.akhir <= 20 ? "text-amber-600" : "text-emerald-700"}`}>
                      {numberFmt.format(item.akhir)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 lg:col-span-2 xl:col-span-3">
          <div className="flex min-w-0 flex-col gap-4">
            <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)]">
              <div className="mb-1 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-800">Aktivitas {year}</h2>
                <Link to="/statistik" className="text-xs font-medium text-primary">Statistik</Link>
              </div>
              <p className="mb-2 text-[11px] text-slate-400">Perawatan, audit, ATK, dan inventaris baru</p>
              <div className="h-36 w-full min-w-0">
                {chartData.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    {loading ? "Memuat grafik…" : "Belum ada aktivitas"}
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                      <Tooltip content={<ChartTip />} />
                      <Area type="monotone" dataKey="perawatan" name="Perawatan" stroke="#6E63F6" fill="#6E63F6" fillOpacity={0.14} strokeWidth={2} />
                      <Area type="monotone" dataKey="audit" name="Audit" stroke="#e11d48" fill="#e11d48" fillOpacity={0.08} strokeWidth={2} />
                      <Area type="monotone" dataKey="atk" name="ATK" stroke="#d97706" fill="#d97706" fillOpacity={0.08} strokeWidth={2} />
                      <Area type="monotone" dataKey="inventaris" name="Inventaris" stroke="#059669" fill="#059669" fillOpacity={0.08} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </section>

            <section className="rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)]">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Stok material menipis</h2>
                  <p className="mt-0.5 text-[11px] text-slate-400">Di bawah batas minimum gudang</p>
                </div>
                <button type="button" className="sip-link" onClick={() => setMaterialOpen(true)}>
                  Lihat
                </button>
              </div>
              {loading ? (
                <p className="text-sm text-slate-400">Memuat material…</p>
              ) : materialWatch.rows.length === 0 ? (
                <p className="text-sm text-slate-500">Belum ada material gedung.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 text-[11px] font-semibold">
                  <span className="rounded-full bg-rose-50 px-2.5 py-1 text-rose-700">Habis {materialWatch.habis}</span>
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-800">Menipis {materialWatch.menipis}</span>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">Aman {materialWatch.aman}</span>
                </div>
              )}
            </section>
          </div>

          <section className="relative min-h-[16rem] min-w-0 tablet:h-full tablet:min-h-0">
            <div className="flex h-full min-h-[16rem] flex-col overflow-hidden rounded-[1.4rem] bg-white p-4 shadow-[0_10px_28px_rgba(90,80,160,0.07)] tablet:absolute tablet:inset-0 tablet:min-h-0">
              <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Material gedung</h2>
                  <p className="mt-0.5 text-[11px] text-slate-400">Sisa di gudang dan yang sudah dipakai</p>
                </div>
                <Link to="/material-keluar" className="shrink-0 text-xs font-medium text-primary">Pakai</Link>
              </div>
              {loading ? (
                <p className="text-sm text-slate-400">Memuat material…</p>
              ) : materialRows.length === 0 ? (
                <p className="rounded-2xl bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                  Belum ada material gedung.
                </p>
              ) : (
                <div className="min-h-0 flex-1 overflow-auto">
                  <table className="w-full min-w-[18rem] text-left text-sm">
                  <thead className="sticky top-0 bg-white text-[11px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="pb-2 pr-3 font-medium">Barang</th>
                      <th className="pb-2 pr-3 text-right font-medium">Gudang</th>
                      <th className="pb-2 text-right font-medium">Pakai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialRows.map((item) => (
                      <tr key={item.id} className="border-t border-slate-100">
                        <td className="py-2 pr-3">
                          <span className="block truncate font-medium text-slate-800">{item.nama}</span>
                          <span className="text-[11px] text-slate-400">{item.kategori || ""}</span>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                          {numberFmt.format(Number(item.stok_gudang) || 0)}
                          <span className="ml-1 text-[11px] text-slate-400">{item.satuan || ""}</span>
                        </td>
                        <td className="py-2 text-right tabular-nums text-slate-700">
                          {numberFmt.format(Number(item.terdistribusi) || 0)}
                          <span className="ml-1 text-[11px] text-slate-400">{item.satuan || ""}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </section>
        </div>
      </div>

      <Dialog
        visible={materialOpen}
        onHide={() => setMaterialOpen(false)}
        header="Stok material menipis"
        appendTo={typeof document !== "undefined" ? document.body : null}
        baseZIndex={9999}
        modal
        draggable={false}
        style={{ width: "95vw", maxWidth: "520px" }}
      >
        {lowMaterials.length === 0 ? (
          <p className="py-2 text-sm text-slate-500">Semua material masih di atas batas minimum.</p>
        ) : (
          <ul className="max-h-[60vh] space-y-2 overflow-auto py-1">
            {lowMaterials.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{item.nama}</p>
                  <p className="text-[11px] text-slate-400">
                    {item.kategori || "Material"} · min {numberFmt.format(item.minimal)}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className={`text-sm font-semibold tabular-nums ${item.status === "habis" ? "text-rose-600" : "text-amber-700"}`}>
                    {numberFmt.format(item.stok)} {item.satuan || ""}
                  </p>
                  <p className="text-[11px] font-medium text-slate-500">{item.status === "habis" ? "Habis" : "Menipis"}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </div>
  );
}

export default HomePage;
